import { useState } from 'react';
import BorderHorizontalIcon from '@mui/icons-material/BorderHorizontal';
import BorderVerticalIcon from '@mui/icons-material/BorderVertical';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import LightTooltip from 'src/common/components/styled/LightTooltip';
import { IBlockSpacing, IBlockSpacingSides } from 'src/interfaces/PageBlock';

type SpacingLinkMode = 'individual' | 'horizontal' | 'vertical' | 'all';

const SIDE_KEYS: (keyof IBlockSpacingSides)[] = ['top', 'right', 'bottom', 'left'];

const toggleSx = {
  px: 0.75,
  py: 0.5,
  '&.Mui-selected': {
    borderColor: 'primary.main',
  },
  '&:not(.Mui-selected)': {
    color: 'text.secondary',
  },
} as const;

function sideLabelKey(side: keyof IBlockSpacingSides): string {
  return `system.pages.blockSpacing${side.charAt(0).toUpperCase()}${side.slice(1)}`;
}

function applyLinkedValue(
  sides: IBlockSpacingSides,
  side: keyof IBlockSpacingSides,
  value: number | undefined,
  linkMode: SpacingLinkMode,
): IBlockSpacingSides {
  const next = { ...sides, [side]: value };
  if (linkMode === 'all') {
    return { top: value, right: value, bottom: value, left: value };
  }
  if (linkMode === 'horizontal' && (side === 'left' || side === 'right')) {
    return { ...next, left: value, right: value };
  }
  if (linkMode === 'vertical' && (side === 'top' || side === 'bottom')) {
    return { ...next, top: value, bottom: value };
  }
  return next;
}

function hasPositiveSide(sides?: IBlockSpacingSides): boolean {
  return SIDE_KEYS.some(key => (sides?.[key] ?? 0) > 0);
}

function mergeSpacingSection(
  current: IBlockSpacing | undefined,
  section: 'margin' | 'padding',
  sides: IBlockSpacingSides | undefined,
): IBlockSpacing | undefined {
  const next: IBlockSpacing = {
    ...(current?.margin ? { margin: current.margin } : {}),
    ...(current?.padding ? { padding: current.padding } : {}),
  };
  if (hasPositiveSide(sides)) {
    next[section] = sides;
  } else {
    delete next[section];
  }
  return next.margin || next.padding ? next : undefined;
}

function parseInputValue(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

function formatInputValue(value: number | undefined): string {
  return value != null && value > 0 ? String(value) : '';
}

interface SpacingSectionProps {
  label: string;
  sides?: IBlockSpacingSides;
  linkMode: SpacingLinkMode;
  onLinkModeChange: (mode: SpacingLinkMode) => void;
  onSidesChange: (sides: IBlockSpacingSides | undefined) => void;
}

function SpacingSection({
  label,
  sides = {},
  linkMode,
  onLinkModeChange,
  onSidesChange,
}: SpacingSectionProps) {
  const { t } = useTranslation();

  const handleSideChange = (side: keyof IBlockSpacingSides, raw: string) => {
    const value = parseInputValue(raw);
    const next = applyLinkedValue(sides, side, value, linkMode);
    onSidesChange(hasPositiveSide(next) ? next : undefined);
  };

  const linkOptions: { value: SpacingLinkMode; icon: React.ReactNode; labelKey: string }[] = [
    { value: 'individual', icon: <TuneIcon fontSize="small" />, labelKey: 'blockSpacingLinkIndividual' },
    {
      value: 'horizontal',
      icon: <BorderHorizontalIcon fontSize="small" />,
      labelKey: 'blockSpacingLinkHorizontal',
    },
    {
      value: 'vertical',
      icon: <BorderVerticalIcon fontSize="small" />,
      labelKey: 'blockSpacingLinkVertical',
    },
    { value: 'all', icon: <CropSquareIcon fontSize="small" />, labelKey: 'blockSpacingLinkAll' },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="subtitle2">{label}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={linkMode}
          aria-label={t('system.pages.blockSpacingLinkMode')}
          onChange={(_, value) => {
            if (value) onLinkModeChange(value as SpacingLinkMode);
          }}
        >
          {linkOptions.map(({ value, icon, labelKey }) => {
            const optionLabel = t(`system.pages.${labelKey}`);
            return (
              <LightTooltip key={value} title={optionLabel} placement="top">
                <ToggleButton value={value} aria-label={optionLabel} sx={toggleSx}>
                  {icon}
                </ToggleButton>
              </LightTooltip>
            );
          })}
        </ToggleButtonGroup>
      </Box>
      <Grid container spacing={1.5}>
        {SIDE_KEYS.map(side => (
          <Grid key={side} size={{ xs: 6, sm: 3 }}>
            <TextField
              type="number"
              size="small"
              fullWidth
              label={t(sideLabelKey(side))}
              value={formatInputValue(sides[side])}
              onChange={e => handleSideChange(side, e.target.value)}
              slotProps={{
                htmlInput: { min: 0, step: 1 },
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

interface BlockSpacingControlsProps {
  value?: IBlockSpacing;
  onChange: (value: IBlockSpacing | undefined) => void;
}

export default function BlockSpacingControls({ value, onChange }: BlockSpacingControlsProps) {
  const { t } = useTranslation();
  const [marginLinkMode, setMarginLinkMode] = useState<SpacingLinkMode>('individual');
  const [paddingLinkMode, setPaddingLinkMode] = useState<SpacingLinkMode>('individual');

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('system.pages.blockSpacingThemeUnitsHint')}
      </Typography>
      <SpacingSection
        label={t('system.pages.blockSpacingMargin')}
        sides={value?.margin}
        linkMode={marginLinkMode}
        onLinkModeChange={setMarginLinkMode}
        onSidesChange={margin => onChange(mergeSpacingSection(value, 'margin', margin))}
      />
      <SpacingSection
        label={t('system.pages.blockSpacingPadding')}
        sides={value?.padding}
        linkMode={paddingLinkMode}
        onLinkModeChange={setPaddingLinkMode}
        onSidesChange={padding => onChange(mergeSpacingSection(value, 'padding', padding))}
      />
    </Box>
  );
}
